import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Search, CheckCircle2, XCircle, AlertCircle, Info, Database, RefreshCw, PlayCircle } from 'lucide-react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('FieldDiscovery', 'encompass-integration');

interface DiscoveryResult {
  fieldId: string;
  fieldName: string;
  description: string;
  schemaApiPath: string;
  discoveredPath: string;
  pathMatches: boolean;
  autoCorrected?: boolean;
  confidence: number;
  discoveryMethod: string;
  sampleCount: number;
  successCount: number;
  sampleValues: Array<{
    loanNumber: string;
    loanFolder: string;
    value: unknown;
    found: boolean;
  }>;
  folderBreakdown: Record<string, {
    sampleCount: number;
    successCount: number;
    confidence: number;
    samples: Array<{
      loanNumber: string;
      loanFolder: string;
      value: unknown;
      found: boolean;
    }>;
  }>;
  suggestedColumnName: string;
  dataType: string;
  format: string;
  fetchedFromApi?: boolean;
}

interface ExistingField {
  id: string;
  field_id: string;
  field_name: string;
  column_name: string;
  is_active: boolean;
  created_at: string;
}

interface DiscoveredField {
  id: string;
  field_id: string;
  field_name: string;
  column_name: string;
  created_at: string;
  confidence_score: number;
  processed_loans?: number;    // Loans that have been processed
  total_loans?: number;        // Total loans to process
  successful_extractions?: number; // Loans where field had value
  backfill_status?: 'no_data' | 'running' | 'completed' | 'failed' | 'pending' | 'needs_resume';
  job_id?: string;
  last_run_at?: string;        // When last backfill was run
  last_run_by_email?: string;  // Email of who ran it
}

interface BackfillStatusResponse {
  statuses?: Array<{
    fieldId: string;
    processedLoans: number;
    totalLoans: number;
    successfulExtractions: number;
    backfillStatus: DiscoveredField['backfill_status'];
    jobId: string | null;
    lastRunAt: string | null;
    lastRunByEmail: string | null;
  }>;
}

interface FunctionErrorLike {
  message?: string;
  status?: number;
  context?: {
    body?: unknown;
  };
}

interface FunctionErrorBody {
  error?: string;
  details?: string;
  suggestion?: string;
  apiIssue?: boolean;
  critical?: boolean;
}

function asFunctionError(error: unknown): FunctionErrorLike {
  if (error && typeof error === 'object') {
    return error as FunctionErrorLike;
  }

  return { message: String(error) };
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  const functionError = asFunctionError(error);
  return new Error(functionError.message || 'Unknown error');
}

function parseFunctionErrorBody(body: unknown): FunctionErrorBody | null {
  if (!body) {
    return null;
  }

  try {
    if (typeof body === 'string') {
      return JSON.parse(body) as FunctionErrorBody;
    }

    if (typeof body === 'object') {
      return body as FunctionErrorBody;
    }
  } catch {
    return null;
  }

  return null;
}

export default function FieldDiscovery() {
  const [fieldId, setFieldId] = useState('');
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState<DiscoveryResult | null>(null);
  const [existingField, setExistingField] = useState<ExistingField | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Backfill tab state
  const [discoveredFields, setDiscoveredFields] = useState<DiscoveredField[]>([]);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState<string | null>(null); // fieldId of currently backfilling field
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const [backfillSuccess, setBackfillSuccess] = useState<string | null>(null);
  const [showCompletedFields, setShowCompletedFields] = useState(false); // Toggle for completed fields

  // Check if field already exists in the system
  const checkExistingField = async (fieldIdToCheck: string) => {
    setIsCheckingExisting(true);
    try {
      const { data, error: queryError } = await supabase
        .from('encompass_discovered_fields')
        .select('id, field_id, field_name, column_name, is_active, created_at')
        .eq('field_id', fieldIdToCheck)
        .maybeSingle();

      if (!queryError && data) {
        setExistingField(data);
        return data;
      }
      setExistingField(null);
      return null;
    } catch {
      setExistingField(null);
      return null;
    } finally {
      setIsCheckingExisting(false);
    }
  };

  const handleDiscover = async () => {
    if (!fieldId.trim()) {
      setError('Please enter a field ID');
      return;
    }

    setIsDiscovering(true);
    setError(null);
    setSuccessMessage(null);
    setDiscoveryResult(null);

    // Check if field already exists
    const existing = await checkExistingField(fieldId.trim());
    if (existing) {
      setError(`Field ${fieldId} has already been added to the system as column "${existing.column_name}"`);
      setIsDiscovering(false);
      return;
    }

    try {
      const { data, error: fnError } = await supabase.functions.invoke('discover-encompass-field', {
        body: {
          fieldId: fieldId.trim(),
          sampleSize: 5,
          loanFolder: 'Prospect'
        }
      });

      if (fnError) {
        throw fnError;
      }

      setDiscoveryResult(data);
    } catch (err: unknown) {
      const functionError = asFunctionError(err);
      log.error('Discovery error', { fieldId }, normalizeError(err));

      // Properly extract error from Supabase function response
      let errorMessage = 'Failed to discover field';
      let errorDetails = '';
      let suggestion = '';

      if (functionError.context?.body) {
        // Parse the actual error response
        const errorData = parseFunctionErrorBody(functionError.context.body);
        if (errorData) {
          errorMessage = errorData.error || errorMessage;
          errorDetails = errorData.details || '';
          suggestion = errorData.suggestion || '';

          // For FE field issues, show specific guidance
          if (errorData.apiIssue || errorData.critical) {
            const issueType = errorData.apiIssue ? '⚠️ API Issue' : '🚨 Critical';
            errorMessage = `${issueType}: ${errorMessage}`;
          }
        } else {
          // Fallback if parsing fails
          errorMessage = functionError.message || errorMessage;
        }
      }

      // Build comprehensive error message
      const fullError = [errorMessage, errorDetails, suggestion]
        .filter(Boolean)
        .join('\n\n');

      setError(fullError);
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleAddToSystem = async () => {
    if (!discoveryResult) {
      return;
    }

    setIsAdding(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('add-discovered-field', {
        body: {
          fieldId: discoveryResult.fieldId,
          jsonPath: discoveryResult.discoveredPath,
          columnName: discoveryResult.suggestedColumnName,
          storageType: 'column',  // Use column storage (not jsonb)
          fieldName: discoveryResult.fieldName,
          description: discoveryResult.description,
          dataType: discoveryResult.dataType,
          format: discoveryResult.format,
          confidence: discoveryResult.confidence,
          sampleCount: discoveryResult.sampleCount,
          successCount: discoveryResult.successCount
        }
      });

      if (fnError) {
        // Handle 409 Conflict - Field already exists
        if (fnError.status === 409 || fnError.message?.includes('409')) {
          const existingFieldMsg = `Field ${discoveryResult.fieldId} (${discoveryResult.fieldName}) has already been added to the system.\n\nThis field is already configured and may have data populated. If you need to update existing loan data, use the backfill function instead.`;
          setError(existingFieldMsg);

          // Also check if field exists in the database
          await checkExistingField(discoveryResult.fieldId);
        } else if (fnError.message?.includes('validationErrors')) {
          // Handle validation errors
          const errorData = JSON.parse(fnError.message);
          setError(errorData.details || errorData.error);
        } else {
          setError(fnError.message || 'Failed to add field to system');
        }
        throw fnError;
      }

      // Show detailed success message
      const successMsg = data.message || `✅ Field ${discoveryResult.fieldId} added successfully!`;

      setSuccessMessage(successMsg);

      // Reset form after 5 seconds
      setTimeout(() => {
        setFieldId('');
        setDiscoveryResult(null);
        setSuccessMessage(null);
      }, 5000);

    } catch (err: unknown) {
      const functionError = asFunctionError(err);
      log.error('Add field error', { fieldId: discoveryResult?.fieldId }, normalizeError(err));

      // Parse Edge Function error responses
      if (!error && functionError.context?.body) {
        const errorData = parseFunctionErrorBody(functionError.context.body);
        if (errorData) {
          // Check for duplicate field error from Edge Function
          if (errorData.error?.includes('already exists') || errorData.error?.includes('duplicate')) {
            const existingFieldMsg = `Field ${discoveryResult.fieldId} already exists in the system.\n\nColumn: ${discoveryResult.suggestedColumnName}\n\nTo update existing loan data with this field, use the backfill function.`;
            setError(existingFieldMsg);
            await checkExistingField(discoveryResult.fieldId);
          }
        }
      }
    } finally {
      setIsAdding(false);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.75) {
      return 'text-green-600';
    }
    if (confidence >= 0.5) {
      return 'text-yellow-600';
    }
    return 'text-red-600';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.75) {
      return 'HIGH CONFIDENCE';
    }
    if (confidence >= 0.5) {
      return 'MEDIUM CONFIDENCE';
    }
    return 'LOW CONFIDENCE';
  };

  // Load all discovered fields for backfill tab
  const loadDiscoveredFields = async () => {
    setIsLoadingFields(true);
    setBackfillError(null);

    try {
      // Get all discovered fields
      const { data: fields, error: fieldsError } = await supabase
        .from('encompass_discovered_fields')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (fieldsError) {
        throw fieldsError;
      }

      // Get total loan count once (all loans, not just Prospect)
      const { count: totalLoansCount, error: countError } = await supabase
        .from('encompass_loans')
        .select('id', { count: 'exact' });

      if (countError) {
        log.warn('Failed to count Encompass loans for Field Discovery', {}, normalizeError(countError));
      }

      const defaultTotalLoans = totalLoansCount || 0;

      const fieldIds = (fields || []).map((field) => field.field_id);
      const { data: statusData, error: statusError } =
        await supabase.functions.invoke<BackfillStatusResponse>('get-field-backfill-statuses', {
          body: { fieldIds, defaultTotalLoans }
        });

      if (statusError) {
        throw statusError;
      }

      const statusByFieldId = new Map(
        (statusData?.statuses || []).map((status) => [status.fieldId, status])
      );

      const fieldsWithStatus = (fields || []).map((field) => {
        const status = statusByFieldId.get(field.field_id);

        return {
          ...field,
          processed_loans: status?.processedLoans ?? 0,
          total_loans: status?.totalLoans ?? defaultTotalLoans,
          successful_extractions: status?.successfulExtractions ?? 0,
          backfill_status: status?.backfillStatus ?? 'pending',
          job_id: status?.jobId ?? undefined,
          last_run_at: status?.lastRunAt ?? undefined,
          last_run_by_email: status?.lastRunByEmail ?? undefined
        } as DiscoveredField;
      });

      setDiscoveredFields(fieldsWithStatus);
    } catch (err: unknown) {
      log.error('Failed to load discovered fields', {}, normalizeError(err));
      setBackfillError('Failed to load discovered fields');
    } finally {
      setIsLoadingFields(false);
    }
  };

  // Trigger backfill for a specific field
  const triggerBackfill = async (fieldId: string) => {
    setIsBackfilling(fieldId);
    setBackfillError(null);
    setBackfillSuccess(null);

    try {
      const { error: fnError } = await supabase.functions.invoke('trigger-field-backfill', {
        body: { fieldId }
      });

      if (fnError) {
        throw fnError;
      }

      setBackfillSuccess(`Backfill started for field ${fieldId}`);

      // Clear success message after 3 seconds
      setTimeout(() => setBackfillSuccess(null), 3000);

      // Reload fields to show updated status
      await loadDiscoveredFields();

      // Enable auto-refresh to track progress
      setAutoRefresh(true);
    } catch (err: unknown) {
      const functionError = asFunctionError(err);
      log.error('Backfill trigger error', { fieldId }, normalizeError(err));
      setBackfillError(`Failed to start backfill: ${functionError.message || 'Unknown error'}`);
    } finally {
      setIsBackfilling(null);
    }
  };

  // Trigger backfill for all fields with no data
  const triggerBackfillAll = async () => {
    const fieldsToBackfill = discoveredFields.filter(f =>
      f.backfill_status === 'no_data' ||
      f.backfill_status === 'failed' ||
      f.backfill_status === 'needs_resume' ||
      f.backfill_status === 'pending'
    );

    if (fieldsToBackfill.length === 0) {
      setBackfillError('No fields need backfilling');
      return;
    }

    setIsBackfilling('all');
    setBackfillError(null);
    setBackfillSuccess(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('trigger-field-backfill', {
        body: {
          fieldIds: fieldsToBackfill.map(f => f.field_id)
        }
      });

      if (fnError) {
        throw fnError;
      }

      // Use edge function's formatted message instead of generic one
      const summary = data as { message: string; successCount: number; failedCount: number; alreadyRunning: number };
      setBackfillSuccess(summary.message || `Started backfill for ${fieldsToBackfill.length} fields`);

      // Clear success message after 5 seconds (longer for detailed message)
      setTimeout(() => setBackfillSuccess(null), 5000);

      // Reload fields to show updated status
      await loadDiscoveredFields();

      // Enable auto-refresh to track progress
      setAutoRefresh(true);
    } catch (err: unknown) {
      const functionError = asFunctionError(err);
      log.error('Bulk backfill error', { fieldCount: fieldsToBackfill.length }, normalizeError(err));
      setBackfillError(`Failed to start bulk backfill: ${functionError.message || 'Unknown error'}`);
    } finally {
      setIsBackfilling(null);
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) {
return;
}

    const interval = setInterval(() => {
      loadDiscoveredFields();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Auto-disable refresh when all jobs are complete
  useEffect(() => {
    // Skip if data is still loading - prevents false-positive auto-disable during tab switches
    if (isLoadingFields) {
return;
}
    // Skip if no data loaded yet
    if (discoveredFields.length === 0) {
return;
}

    // Check for any incomplete jobs, not just running
    const hasIncompleteJobs = discoveredFields.some(f =>
      f.backfill_status === 'running' ||
      f.backfill_status === 'needs_resume' ||
      f.backfill_status === 'failed' ||
      f.backfill_status === 'pending'
    );

    // Turn off auto-refresh if it's on but no incomplete jobs remain
    if (autoRefresh && !hasIncompleteJobs) {
      setAutoRefresh(false);
      setBackfillSuccess('All backfill jobs completed');
    }
  }, [discoveredFields, autoRefresh, isLoadingFields]);

  // Load fields when component mounts
  useEffect(() => {
    loadDiscoveredFields();
  }, []);

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Encompass Field Discovery</h1>
        <p className="text-muted-foreground mt-2">
          Discover and add Encompass fields to your system systematically
        </p>
      </div>

      <Tabs defaultValue="discover" className="w-full" onValueChange={(value) => {
        if (value === 'backfill') {
          loadDiscoveredFields(); // Reload data when switching to backfill tab
        }
      }}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="discover">Discover</TabsTrigger>
          <TabsTrigger value="backfill">
            Backfill Data
            {discoveredFields.filter(f => f.backfill_status !== 'completed' && f.backfill_status !== 'running').length > 0 && (
              <Badge className="ml-2" variant="destructive">
                {discoveredFields.filter(f => f.backfill_status !== 'completed' && f.backfill_status !== 'running').length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="discover" className="space-y-6">
          {/* Discovery Form */}
          <Card>
        <CardHeader>
          <CardTitle>Discover Field</CardTitle>
          <CardDescription>
            Enter an Encompass field ID to discover where it exists in loan data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="fieldId">Field ID</Label>
              <Input
                id="fieldId"
                placeholder="e.g., 315, 3238, 1109"
                value={fieldId}
                onChange={(e) => setFieldId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleDiscover()}
                disabled={isDiscovering}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleDiscover}
                disabled={isDiscovering || isCheckingExisting || !fieldId.trim()}
                className="w-32"
              >
                {isDiscovering || isCheckingExisting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isCheckingExisting ? 'Checking...' : 'Searching...'}
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Discover
                  </>
                )}
              </Button>
            </div>
          </div>

          {existingField && (
            <Alert className="mt-4 border-blue-600 bg-blue-50">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700">
                <strong>Field {existingField.field_id} already exists</strong> as column "{existingField.column_name}".
                Added on {new Date(existingField.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric'
                })}.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {successMessage && (
            <Alert className="mt-4 border-green-600 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-600">{successMessage}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Discovery Results */}
      {discoveryResult && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Discovery Results</CardTitle>
                <CardDescription>Field {discoveryResult.fieldId}</CardDescription>
              </div>
              <div className={`text-sm font-semibold ${getConfidenceColor(discoveryResult.confidence)}`}>
                {getConfidenceLabel(discoveryResult.confidence)}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Discovery Badges */}
            {(discoveryResult.fetchedFromApi || discoveryResult.autoCorrected) && (
              <div className="flex gap-2">
                {discoveryResult.fetchedFromApi && (
                  <Badge variant="outline" className="border-purple-600 text-purple-700 bg-purple-50">
                    <Database className="mr-1 h-3 w-3" />
                    Fetched from Encompass API
                  </Badge>
                )}
                {discoveryResult.autoCorrected && (
                  <Badge variant="outline" className="border-blue-600 text-blue-700 bg-blue-50">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    Auto-Corrected Path
                  </Badge>
                )}
              </div>
            )}

            {/* Field Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Field Name</Label>
                <p className="font-medium">{discoveryResult.fieldName}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Description</Label>
                <p className="font-medium">{discoveryResult.description}</p>
              </div>
            </div>

            {/* Paths */}
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                <div>
                  <Label className="text-muted-foreground text-xs">Schema API Path</Label>
                  <p className="font-mono text-sm">{discoveryResult.schemaApiPath}</p>
                </div>
                {discoveryResult.pathMatches ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
              {discoveryResult.discoveredPath !== discoveryResult.schemaApiPath && (
                <div className="p-3 bg-blue-50 rounded">
                  <Label className="text-muted-foreground text-xs">Discovered Path (Corrected)</Label>
                  <p className="font-mono text-sm text-blue-700">{discoveryResult.discoveredPath}</p>
                </div>
              )}
            </div>

            {/* Confidence Bar */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-muted-foreground">Confidence Score</Label>
                <span className="text-sm font-semibold">{(discoveryResult.confidence * 100).toFixed(0)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    discoveryResult.confidence >= 0.75
                      ? 'bg-green-600'
                      : discoveryResult.confidence >= 0.5
                      ? 'bg-yellow-600'
                      : 'bg-red-600'
                  }`}
                  style={{ width: `${discoveryResult.confidence * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Found in {discoveryResult.successCount} of {discoveryResult.sampleCount} sample loans
              </p>
            </div>

            {/* Folder Breakdown */}
            {discoveryResult.folderBreakdown && Object.keys(discoveryResult.folderBreakdown).length > 0 && (
              <div>
                <Label className="text-muted-foreground mb-2 block">Results by Loan Folder</Label>
                <div className="grid gap-2">
                  {Object.entries(discoveryResult.folderBreakdown).map(([folder, stats]) => (
                    <div key={folder} className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center gap-3">
                        {stats.confidence >= 0.5 ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-gray-400" />
                        )}
                        <span className="font-medium">{folder}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">
                          {stats.successCount}/{stats.sampleCount} loans
                        </span>
                        <span className={`font-semibold ${
                          stats.confidence >= 0.75 ? 'text-green-600' :
                          stats.confidence >= 0.5 ? 'text-yellow-600' :
                          'text-gray-400'
                        }`}>
                          {(stats.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sample Data Table */}
            <div>
              <Label className="text-muted-foreground mb-2 block">All Sample Data</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-medium">Folder</th>
                      <th className="text-left p-3 font-medium">Loan Number</th>
                      <th className="text-left p-3 font-medium">Value</th>
                      <th className="text-center p-3 font-medium">Found?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discoveryResult.sampleValues.map((sample, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-3 text-xs">{sample.loanFolder || 'Unknown'}</td>
                        <td className="p-3 font-mono text-xs">{sample.loanNumber || '-'}</td>
                        <td className="p-3 font-mono text-xs">
                          {sample.found ? String(sample.value) : '-'}
                        </td>
                        <td className="p-3 text-center">
                          {sample.found ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600 inline" />
                          ) : (
                            <XCircle className="h-4 w-4 text-gray-400 inline" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Suggestions */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded">
              <div>
                <Label className="text-muted-foreground text-xs">Suggested Column</Label>
                <p className="font-mono text-sm">{discoveryResult.suggestedColumnName}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Data Type</Label>
                <p className="text-sm">{discoveryResult.dataType}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Format</Label>
                <p className="text-sm">{discoveryResult.format}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setDiscoveryResult(null);
                  setFieldId('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddToSystem}
                disabled={isAdding}
                className="min-w-32"
              >
                {isAdding ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add to System'
                )}
              </Button>
            </div>

            {(discoveryResult.warning || discoveryResult.confidence < 0.5) && (
              <Alert className="border-yellow-600 bg-yellow-50">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-700">
                  {discoveryResult.warning ||
                   'Low confidence detected. This field may not appear in all loan types (e.g., Purchase Price won\'t appear in refinances). Verify the field is correct before adding.'}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="backfill" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Field Backfill Management</CardTitle>
                  <CardDescription>
                    Fields that need backfilling - completed fields are hidden
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadDiscoveredFields}
                    disabled={isLoadingFields}
                  >
                    {isLoadingFields ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    <span className="ml-2">Refresh</span>
                  </Button>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Auto-refresh
                  </label>
                  <Button
                    onClick={triggerBackfillAll}
                    disabled={isBackfilling === 'all' || discoveredFields.filter(f => f.backfill_status === 'no_data' || f.backfill_status === 'failed' || f.backfill_status === 'needs_resume' || f.backfill_status === 'pending').length === 0}
                  >
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Backfill All Missing
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Sticky alerts so they stay visible when table re-renders */}
              {(backfillSuccess || backfillError) && (
                <div className="sticky top-0 z-10 bg-white pb-2">
                  {backfillSuccess && (
                    <Alert className="mb-2 border-green-600 bg-green-50">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-600">{backfillSuccess}</AlertDescription>
                    </Alert>
                  )}

                  {backfillError && (
                    <Alert variant="destructive" className="mb-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{backfillError}</AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              <div className="mb-4 flex justify-between items-center">
                <div className="flex gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">
                      {discoveredFields.filter(f => f.backfill_status === 'pending' || f.backfill_status === 'failed').length}
                    </Badge>
                    <span>Need Backfill</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-orange-600">
                      {discoveredFields.filter(f => f.backfill_status === 'needs_resume').length}
                    </Badge>
                    <span>Need Resume</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-yellow-600">
                      {discoveredFields.filter(f => f.backfill_status === 'running').length}
                    </Badge>
                    <span>Running</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-600">
                      {discoveredFields.filter(f => f.backfill_status === 'completed').length}
                    </Badge>
                    <span>Completed</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCompletedFields(!showCompletedFields)}
                  className="flex items-center gap-2"
                >
                  {showCompletedFields ? (
                    <>Hide Completed</>
                  ) : (
                    <>Show All Fields</>
                  )}
                </Button>
              </div>

              {isLoadingFields ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 font-medium">Field ID</th>
                        <th className="text-left p-3 font-medium">Field Name</th>
                        <th className="text-left p-3 font-medium">Column Name</th>
                        <th className="text-center p-3 font-medium">Progress</th>
                        <th className="text-left p-3 font-medium">Last Run</th>
                        <th className="text-center p-3 font-medium">Status</th>
                        <th className="text-center p-3 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discoveredFields.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center p-8 text-muted-foreground">
                            No discovered fields found
                          </td>
                        </tr>
                      ) : (
                        (() => {
                          const fieldsToShow = showCompletedFields
                            ? discoveredFields
                            : discoveredFields.filter(f => f.backfill_status !== 'completed');

                          if (fieldsToShow.length === 0) {
                            return (
                              <tr>
                                <td colSpan={7} className="text-center p-8 text-muted-foreground">
                                  {showCompletedFields
                                    ? "No fields found"
                                    : "✅ All fields have been successfully backfilled!"}
                                </td>
                              </tr>
                            );
                          }

                          return fieldsToShow.map((field) => (
                          <tr key={field.id} className="border-t">
                            <td className="p-3 font-mono text-sm">{field.field_id}</td>
                            <td className="p-3">{field.field_name}</td>
                            <td className="p-3 font-mono text-sm">{field.column_name}</td>
                            <td className="p-3 text-center">
                              <div className="font-mono text-sm">
                                {field.processed_loans || 0}/{field.total_loans || 0}
                              </div>
                              {field.successful_extractions !== undefined && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {field.successful_extractions} with data
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-sm">
                              {field.last_run_at ? (
                                <div>
                                  <div className="text-muted-foreground">
                                    {new Date(field.last_run_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                  {field.last_run_by_email && (
                                    <div className="text-xs text-muted-foreground">
                                      by {field.last_run_by_email.split('@')[0]}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Never</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {field.backfill_status === 'pending' && (
                                <Badge variant="outline">Pending</Badge>
                              )}
                              {field.backfill_status === 'running' && (
                                <Badge className="bg-yellow-600">
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                  Running
                                </Badge>
                              )}
                              {field.backfill_status === 'completed' && (
                                <Badge className="bg-green-600">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Complete
                                </Badge>
                              )}
                              {field.backfill_status === 'failed' && (
                                <Badge variant="destructive">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Failed
                                </Badge>
                              )}
                              {field.backfill_status === 'needs_resume' && (
                                <Badge className="bg-orange-600">
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                  Resume
                                </Badge>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => triggerBackfill(field.field_id)}
                                disabled={
                                  isBackfilling === field.field_id ||
                                  field.backfill_status === 'running' ||
                                  field.backfill_status === 'completed'
                                }
                              >
                                {isBackfilling === field.field_id ? (
                                  <>
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                    Starting...
                                  </>
                                ) : field.backfill_status === 'completed' ? (
                                  'Completed'
                                ) : field.backfill_status === 'running' ? (
                                  'Running...'
                                ) : field.backfill_status === 'needs_resume' ? (
                                  <>
                                    <RefreshCw className="h-3 w-3 mr-1" />
                                    Resume
                                  </>
                                ) : (
                                  <>
                                    <PlayCircle className="h-3 w-3 mr-1" />
                                    Backfill
                                  </>
                                )}
                              </Button>
                            </td>
                          </tr>
                        ));
                        })()
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
